import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, Clock, Loader2, Package, Truck, Search, ArrowLeft, FileText, Image as ImageIcon, Receipt, ExternalLink, Send, ShieldCheck, MapPin } from "lucide-react";
import LayoutAnnotator from "@/components/LayoutAnnotator";
import AnnotatedCommentViewer from "@/components/AnnotatedCommentViewer";

// PII masking for non-owner viewers (anonymous public tracking).
// Rules per product spec:
//   - Name: first 2 characters visible, rest as '*' (each char), keeps word spaces.
//   - Email: first 2 chars of local part + asterisks + '@' + domain visible.
//   - Phone: first 4 + last 2 digits visible, middle digits masked.
const maskName = (s) => {
  if (!s) return "";
  return s.split(" ").map((w) => w.length <= 2 ? w : w.slice(0, 2) + "*".repeat(Math.max(1, w.length - 2))).join(" ");
};
const maskEmail = (s) => {
  if (!s || !s.includes("@")) return s ? s.slice(0, 2) + "***" : "";
  const [local, domain] = s.split("@");
  if (local.length <= 2) return `${local}***@${domain}`;
  return `${local.slice(0, 2)}${"*".repeat(Math.max(1, local.length - 2))}@${domain}`;
};
const maskPhone = (s) => {
  if (!s) return "";
  const digits = s.replace(/\D/g, "");
  if (digits.length <= 6) return digits;
  const middle = "*".repeat(digits.length - 6);
  return `${digits.slice(0, 4)}${middle}${digits.slice(-2)}`;
};

const STAGES = [
  { key: "pending_payment", label: "Pending payment", icon: Clock },
  { key: "paid", label: "Confirmed Payment", icon: CheckCircle2 },
  { key: "in_production", label: "In production", icon: Package },
  { key: "ready_for_pickup", label: "Ready / Shipped", icon: Truck },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
];
const stageIndex = (status) => {
  if (["ready_for_pickup", "shipped"].includes(status)) return 3;
  return STAGES.findIndex((s) => s.key === status);
};

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const TrackPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const initial = new URLSearchParams(location.search).get("id") || "";
  const [orderId, setOrderId] = useState(initial);
  const [order, setOrder] = useState(null);
  const [layoutReq, setLayoutReq] = useState(null);
  const [comment, setComment] = useState("");
  const [annotFile, setAnnotFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetch = async (id) => {
    if (!id) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      if (data.has_layout_request) {
        try {
          const { data: lr } = await api.get(`/layout-requests/by-order/${id}`);
          setLayoutReq(lr);
        } catch { setLayoutReq(null); }
      } else { setLayoutReq(null); }
    } catch {
      setError("Order not found. Please check your tracking ID.");
      setOrder(null); setLayoutReq(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (initial) fetch(initial); }, [initial]);

  const idx = order ? stageIndex(order.status) : -1;

  // Determine whether to show sensitive sections (design source, receipt details).
  // Visible to: the owner (by user_id or email match) OR admin roles.
  const isOwnerOrAdmin = (() => {
    if (!user || !order) return false;
    const adminRoles = ["super_admin", "front_office", "cashier", "production", "releasing", "accounting"];
    if (adminRoles.includes(user.role)) return true;
    if (order.user_id && order.user_id === user.user_id) return true;
    if ((order.customer_email || "").toLowerCase() === (user.email || "").toLowerCase()) return true;
    return false;
  })();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pav-fade-in" data-testid="track-page">
      {/* Big Back button */}
      <button onClick={() => navigate(-1)}
        className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0B1E3F] hover:text-[#06B6D4] transition-colors"
        data-testid="track-back-button">
        <span className="h-9 w-9 rounded-full border border-[#E2E8F0] bg-white flex items-center justify-center group-hover:border-[#06B6D4]">
          <ArrowLeft className="h-4 w-4" />
        </span>
        Back
      </button>

      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Tracking</p>
        <h1 className="font-display text-4xl font-extrabold text-[#0B1E3F] mt-2">Track your order</h1>
        <p className="text-neutral-600 mt-2">Enter your order ID (e.g. PAV-260205-ABC123)</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); fetch(orderId); }}
        className="flex gap-2 mb-8">
        <input className="pav-input"
          placeholder="PAV-XXXXXX-XXXXXX"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value.toUpperCase())}
          data-testid="track-order-input" />
        <button type="submit" className="btn-primary inline-flex items-center gap-2" data-testid="track-search-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Track
        </button>
      </form>

      {error && <p className="text-red-600" data-testid="track-error">{error}</p>}

      {order && (
        <div className="space-y-6" data-testid="track-result">
          {/* Progress card */}
          <div className="pav-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-neutral-500">Order</p>
                <h2 className="font-display text-2xl font-bold">{order.order_id}</h2>
              </div>
              <span className={`pav-chip status-${order.status}`}>{order.status === "paid" ? "Confirmed Payment" : order.status.replace(/_/g, " ")}</span>
            </div>

            <div className="mt-8">
              <div className="flex justify-between mb-3">
                {STAGES.map((s, i) => {
                  const active = i <= idx;
                  return (
                    <div key={s.key} className="flex flex-col items-center text-center" style={{ width: `${100 / STAGES.length}%` }}>
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${active ? "bg-[#0B1E3F] text-white" : "bg-[#F4F6FB] text-neutral-400"}`}>
                        <s.icon className="h-5 w-5" />
                      </div>
                      <p className={`mt-2 text-xs ${active ? "text-[#0B1E3F] font-semibold" : "text-neutral-400"}`}>{s.label}</p>
                    </div>
                  );
                })}
              </div>
              <div className="h-1 bg-[#F4F6FB] rounded-full overflow-hidden">
                <div className="h-full bg-[#06B6D4] transition-all duration-500" style={{ width: `${((idx + 1) / STAGES.length) * 100}%` }} />
              </div>
            </div>
          </div>

          {/* Order details */}
          <div className="pav-card">
            <h3 className="font-display text-lg font-bold mb-3">Order details</h3>
            <div className="text-sm space-y-2">
              <p data-testid="track-customer-name">
                <span className="text-neutral-500">Customer:</span> {isOwnerOrAdmin ? order.customer_name : maskName(order.customer_name)}
              </p>
              <p data-testid="track-customer-email">
                <span className="text-neutral-500">Email:</span> {isOwnerOrAdmin ? order.customer_email : maskEmail(order.customer_email)}
              </p>
              <p data-testid="track-customer-phone">
                <span className="text-neutral-500">Phone:</span> {isOwnerOrAdmin ? order.customer_phone : maskPhone(order.customer_phone)}
              </p>
              <p><span className="text-neutral-500">Fulfillment:</span> {order.fulfillment_type}{order.pickup_datetime ? ` — pickup ${new Date(order.pickup_datetime).toLocaleString()}` : ""}</p>
              {order.shipping && isOwnerOrAdmin && <p><span className="text-neutral-500">Ship to:</span> {order.shipping.address_line1}, {order.shipping.city}</p>}
            </div>
            <div className="mt-4 divide-y divide-[#E2E8F0] border-t border-b border-[#E2E8F0]">
              {order.items.map((it, idx2) => (
                <div key={idx2} className="py-2 text-sm flex justify-between gap-3">
                  <span className="min-w-0 flex-1">{it.width}×{it.height} {it.unit} • {(it.finishing || "").replace(/_/g, " ")} • × {it.quantity}</span>
                  <span className="shrink-0">₱{(it.line_total || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-3 text-lg font-display font-bold">
              <span>Total</span>
              <span className="text-[#0B1E3F]">{peso(order.total)}</span>
            </div>
          </div>

          {/* Layout Request panel (logged-in owner) */}
          {isOwnerOrAdmin && layoutReq && (
            <div className="pav-card" data-testid="track-layout-request">
              <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
                <ImageIcon className="h-5 w-5" /> Layout request status
              </h3>
              <div className="space-y-1.5 mb-4">
                {(layoutReq.history || []).map((h, i) => (
                  <p key={i} className="text-xs">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#06B6D4]" />
                      <strong className="capitalize">{(h.status || "").replace(/_/g, " ")}</strong>
                      <span className="text-neutral-400">{new Date(h.at).toLocaleString()}</span>
                    </span>
                  </p>
                ))}
              </div>

              {layoutReq.processed_files?.length > 0 && (
                <>
                  <p className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2">Your layout from our designers</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                    {layoutReq.processed_files.map((f, i) => {
                      const isImg = (f.content_type || "").startsWith("image/") ||
                        /\.(png|jpe?g|webp|gif)$/i.test(f.name || "");
                      return (
                        <div key={i} className="rounded-xl border-2 border-[#06B6D4] overflow-hidden bg-white">
                          {isImg ? (
                            <img src={fileUrl(f.url)} alt={f.name} className="w-full h-28 object-cover"
                              data-testid={`track-layout-thumb-${i}`} />
                          ) : (
                            <div className="h-28 flex items-center justify-center bg-[#F4F6FB]"
                              data-testid={`track-layout-thumb-${i}`}><FileText className="h-7 w-7" /></div>
                          )}
                          <p className="px-2 pt-2 text-[11px] truncate font-semibold">{f.name}</p>
                          <div className="px-2 py-1.5 flex items-center gap-1.5">
                            {isImg && layoutReq.status === "for_approval" && layoutReq.user_id === user?.user_id && (
                              <button onClick={() => setAnnotFile(f)}
                                data-testid={`track-annotate-btn-${i}`}
                                className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#06B6D4] text-white hover:bg-[#0891B2] font-semibold">
                                <MapPin className="h-3 w-3" /> Mark up
                              </button>
                            )}
                            <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" download
                              className="text-[10px] text-[#0B1E3F] hover:underline ml-auto inline-flex items-center gap-0.5">
                              Download <ExternalLink className="h-2.5 w-2.5" />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Comment box */}
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-500">Comments / revisions</label>
                <ul className="space-y-1.5 mb-2 max-h-60 overflow-y-auto" data-testid="layout-comments-list">
                  {(layoutReq.comments || []).map((c) => (
                    <li key={c.id} className="text-xs bg-[#F8FAFC] rounded p-2">
                      <p className="font-semibold capitalize">{c.role}:</p>
                      {c.text && <p className="whitespace-pre-wrap">{c.text}</p>}
                      {c.annotations?.length > 0 && (
                        <AnnotatedCommentViewer
                          annotations={c.annotations}
                          imageUrl={c.image_url}
                          testid={`comment-annot-${c.id}`} />
                      )}
                      <p className="text-[10px] text-neutral-400 mt-1">{new Date(c.at).toLocaleString()}</p>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <input className="pav-input flex-1 h-10" placeholder="Type a comment for our designers…"
                    data-testid="layout-comment-input"
                    value={comment} onChange={(e) => setComment(e.target.value)} />
                  <button onClick={async () => {
                    if (!comment.trim()) return;
                    setBusy(true);
                    try {
                      await api.post(`/layout-requests/by-order/${order.order_id}/comment`, { text: comment.trim() });
                      setComment("");
                      const { data } = await api.get(`/layout-requests/by-order/${order.order_id}`);
                      setLayoutReq(data);
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                    finally { setBusy(false); }
                  }}
                  disabled={busy || !comment.trim()}
                  className="btn-outline text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="layout-comment-submit">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>

              {layoutReq.status === "for_approval" && layoutReq.user_id === user?.user_id && (
                <button onClick={async () => {
                  if (!window.confirm("Approve this layout and send the order to production?")) return;
                  setBusy(true);
                  try {
                    await api.post(`/layout-requests/by-order/${order.order_id}/approve`);
                    toast.success("Approved — order is now in production");
                    fetch(order.order_id);
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  finally { setBusy(false); }
                }}
                disabled={busy}
                className="mt-4 w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="layout-approve-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  Approve for Printing
                </button>
              )}
            </div>
          )}

          {/* Design source — logged-in owner/admin only */}
          {isOwnerOrAdmin && (
            <div className="pav-card" data-testid="track-design-source">
              <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
                <ImageIcon className="h-5 w-5" /> Design source
              </h3>
              {order.items.some((it) => it.file_url || it.file_link || it.ai_design_url) ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {order.items.map((it, idx2) => {
                    const src = it.file_url || it.ai_design_url;
                    if (src) {
                      return (
                        <a key={idx2} href={fileUrl(src)} target="_blank" rel="noreferrer"
                          className="block rounded-xl border border-[#E2E8F0] overflow-hidden hover:border-[#06B6D4]"
                          data-testid={`design-thumb-${idx2}`}>
                          <img src={fileUrl(src)} alt="" className="w-full h-24 object-cover"
                            onError={(e) => { e.target.style.display = "none"; }} />
                          <p className="text-[11px] p-2 truncate font-semibold">Item {idx2 + 1}</p>
                        </a>
                      );
                    }
                    if (it.file_link) {
                      return (
                        <a key={idx2} href={it.file_link} target="_blank" rel="noreferrer"
                          className="block rounded-xl border border-[#E2E8F0] p-3 hover:border-[#06B6D4]"
                          data-testid={`design-link-${idx2}`}>
                          <FileText className="h-6 w-6 text-[#0B1E3F] mb-2" />
                          <p className="text-[11px] truncate font-semibold inline-flex items-center gap-1">
                            Item {idx2 + 1} link <ExternalLink className="h-3 w-3" />
                          </p>
                        </a>
                      );
                    }
                    return null;
                  })}
                </div>
              ) : (
                <p className="text-sm text-neutral-500">No design files attached to this order.</p>
              )}
            </div>
          )}

          {/* Receipt customer details — logged-in owner/admin only */}
          {isOwnerOrAdmin && (order.receipt_details || order.customer_address || order.tin_number) && (
            <div className="pav-card" data-testid="track-receipt-details">
              <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
                <Receipt className="h-5 w-5" /> Receipt customer details
              </h3>
              <div className="text-sm space-y-1.5">
                <p><span className="text-neutral-500">Name:</span> {order.receipt_details?.customer_name || order.customer_name}</p>
                <p><span className="text-neutral-500">Address:</span> {order.receipt_details?.address || order.customer_address || "—"}</p>
                <p><span className="text-neutral-500">TIN:</span> {order.receipt_details?.tin || order.tin_number || "—"}</p>
              </div>
            </div>
          )}

          {/* Order Logs (renamed from History) */}
          <div className="pav-card" data-testid="order-logs">
            <h3 className="font-display text-lg font-bold mb-3">Order logs</h3>
            <ul className="space-y-3">
              {(order.history || []).length === 0 && <li className="text-sm text-neutral-500">No log entries yet.</li>}
              {(order.history || []).map((h, idx2) => (
                <li key={idx2} className="flex items-start gap-3 text-sm">
                  <span className="mt-1.5 h-2 w-2 rounded-full bg-[#06B6D4] shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold capitalize">{(h.status || "").replace(/_/g, " ")}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {h.at ? new Date(h.at).toLocaleString() : ""}
                      {h.by ? ` · by ${h.by}` : ""}
                      {h.note ? ` · ${h.note}` : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Annotation modal — opens when customer clicks "Mark up" on a processed file */}
      <LayoutAnnotator
        open={!!annotFile}
        file={annotFile}
        orderId={order?.order_id}
        onClose={() => setAnnotFile(null)}
        onSaved={async () => {
          if (order?.order_id) {
            try {
              const { data } = await api.get(`/layout-requests/by-order/${order.order_id}`);
              setLayoutReq(data);
            } catch { /* ignore */ }
          }
        }}
      />
    </div>
  );
};

export default TrackPage;
